import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ReCAPTCHA from 'react-google-recaptcha';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import { GOOGLE_RECAPTCHA_SITE_KEY } from 'config';
import { useFormState, FormDataType } from 'contexts/FormProvider';
import { FormTextFieldProvider } from 'contexts/FormTextFieldContext';

import { Header, CallUs, StepProgress } from 'components';
import AlertBanner from 'components/AlertBanner/AlertBanner';
import InternalErrorMessage from 'components/ErrorHandler/InternalErrorMessage';
import Loading from 'components/Loading/Loading';

import { FormStep, FormStepContentProps } from 'types';

import CustomerFooter from './common/components/Footer';

import useHttpClient, { headers } from 'hooks/useHttpClient';
import { useStep } from 'hooks/useStep';
import { useTrackPageView, trackAxiosResponse, useTrackAbandon } from 'hooks/useAnalytics';

import { CUSTOMER_FORM_STEP2, CUSTOMER_FORM_STEP3, CUSTOMER_FORM_FIN_STEP5 } from 'utils/constants';

import { formatDate, isObjectContainsXss } from 'utils/utils';
import { remap } from 'utils/remap';

import { nonFinancialSteps, financialSteps } from 'pages/lodgementSteps';
import { PageLayout, qbeTheme as theme } from '@qbe/ads-core';
import { useContent, useContentContext } from '@qbe/dfc-content';

const Customer = () => {
  const { formData, updateForm, subjectPrefix, isFNH, defaultErrorMessage } = useFormState();

  const { getJSXContent, getTextContent } = useContent({ context: useContentContext() });
  const recaptchaRef = React.useRef<typeof ReCAPTCHA>(null); // react-google-recaptcha doesn't support typescript

  const steps: FormStep[] = !isFNH ? nonFinancialSteps : financialSteps;

  const { currentStep, nextStep, prevStep } = useStep(isFNH ? 1 : 0, steps.length);

  const lengthIndicator = (value: number) => currentStep === steps.length - value;

  // non-financial steps should be rendered in step 1
  const stepsName = (isFNH && currentStep !== 0 ? financialSteps : nonFinancialSteps).map((step, index) => ({
    label: step.name,
    id: step.analyticsName,
    completed: index < currentStep,
  }));
  const stepsAnalytics = steps.map(({ analyticsName }) => analyticsName);

  const renderStepHeader = () => (
    <StepProgress activeStep={currentStep} steps={stepsName} isFinHardship={isFNH && currentStep !== 0} />
  );

  const [isError, setIsError] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  const showErrorAlertBox = React.useCallback(
    (show = true) => {
      if (show) {
        setIsError(true);
        setErrorMsg(defaultErrorMessage);
      } else {
        setIsError(false);
      }
    },
    [defaultErrorMessage]
  );

  const hasSelectedPolicy = Boolean(
    isFNH && get(formData, 'policyOrClaim') === 'policy' && steps[currentStep].name === CUSTOMER_FORM_STEP2
  );

  useTrackPageView(currentStep, stepsAnalytics, formData);
  useTrackAbandon();

  // Localized content constant
  const policyTitle = hasSelectedPolicy ? getTextContent('fha.your.details.policyError.title') : null;
  const policyError = hasSelectedPolicy ? getJSXContent('fha.your.details.policyError.alert') : null;

  const [{ loading, error: apiError }, postData] = useHttpClient(
    {
      url: '/x-digital-remediation-qbe-anzo/api/v1/cms/complaint-forms',
    },
    { manual: true }
  );

  React.useEffect(() => setIsError(hasSelectedPolicy), [hasSelectedPolicy]);

  React.useEffect(() => {
    setIsError(false);

    const error = apiError ? apiError?.response?.data?.errors[0] : null;

    if (!isEmpty(apiError) && isEmpty(error)) {
      showErrorAlertBox();
    }

    if (!isEmpty(error)) {
      showErrorAlertBox();
      trackAxiosResponse(apiError?.response);

      if (error.code === 'ATTACHMENT_OVERSIZE') {
        setErrorMsg(error.message);
      }

      if (error.code === 'ATTACHMENT_INVALID_FILETYPE') {
        setErrorMsg('This file type is not allowed, please attach a different document type');
      }
    }
  }, [apiError, showErrorAlertBox]);

  const showAlertBanner = isError && lengthIndicator(2);

  const isSubmissionStep =
    (isFNH && steps[currentStep].name === CUSTOMER_FORM_FIN_STEP5) ||
    (!isFNH && steps[currentStep].name === CUSTOMER_FORM_STEP3);

  const next = async (newData: Partial<FormDataType>) => {
    updateForm(newData);

    // Submission Step
    if (isSubmissionStep) {
      const newFormData = { ...formData, ...newData };

      if (isObjectContainsXss(newFormData)) {
        showErrorAlertBox();
        return;
      }

      const { files } = newFormData;
      const currentDate = new Date();

      const contents = {
        subject: {
          type: subjectPrefix,
          doc: formatDate(currentDate),
          lastName: newFormData.lastname,
        },
        body: remap(newFormData),
      };

      const contentData = new FormData();
      contentData.append('contents', JSON.stringify(contents));

      if (files) {
        files.forEach((file: File) => contentData.append('attachments', file));
      }

      try {
        setTimeout(() => window.scrollTo(0, 0), 0);
        await recaptchaRef.current.reset();
        const recaptchaToken = await recaptchaRef.current.executeAsync();

        await postData({
          data: contentData,
          params: { submitBy: 'customer', channel: 'email' },
          headers: { ...headers, 'x-recaptcha-token': recaptchaToken },
        });
      } catch (errors) {
        return;
      }
    }

    nextStep();
  };

  const handleClose = () => setIsError(false);

  const renderStepContent = () => {
    const StepContent = steps[currentStep].component as React.ComponentClass<FormStepContentProps>;

    return (
      <ErrorBoundary FallbackComponent={InternalErrorMessage}>
        <StepContent defaultValues={formData} onComplete={next} showError={showErrorAlertBox} />
      </ErrorBoundary>
    );
  };

  return (
    <>
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
        onErrored={showErrorAlertBox}
      />

      <PageLayout
        header={
          <Header
            content={renderStepHeader()}
            onBack={prevStep}
            isBackEnabled={currentStep > 0 && !lengthIndicator(1)}
          />
        }
        footer={<CustomerFooter />}
        background={theme.palette.common.white}
      >
        <Loading show={loading} />
        {currentStep !== 0 && <CallUs isFNH={isFNH} />}
        <FormTextFieldProvider>
          <AlertBanner
            show={showAlertBanner || (isError && hasSelectedPolicy)}
            errorMsg={errorMsg || policyError}
            onClose={handleClose}
            title={policyTitle}
          />
          {renderStepContent()}
        </FormTextFieldProvider>
      </PageLayout>
    </>
  );
};

export default Customer;
